import type { FileMetadata, HeliaNode } from '@/types/poc/helia';
import type { LibP2pWithServices } from '@/types/poc/libp2p';
import type { MessagePayload } from '@/types/poc/message';

export interface FileUploadedResult {
  cid: string;
  metadata: FileMetadata;
  sender: string;
  shouldReplicate: boolean;
  messageTimestamp: number;
}

export interface FilesUpdatedResult {
  sender: string;
  files: Array<{
    cid: string;
    metadata: FileMetadata;
    shouldReplicate: boolean;
    messageTimestamp: number;
  }>;
}

export interface FolderCreatedResult {
  sixId: string;
  folderName: string;
  folderPath: string;
  sender: string;
  messageTimestamp: number;
}

export interface FolderDeletedResult {
  sixId: string;
  folderPath: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileDeletedResult {
  sixId: string;
  fileId: string;
  sender: string;
  messageTimestamp: number;
}

export interface SixCreatedResult {
  sender: string;
  messageTimestamp: number;
}

export interface SixUpdatedResult {
  sender: string;
  messageTimestamp: number;
}

export interface SixDeletedResult {
  sender: string;
  messageTimestamp: number;
}

export interface FileClonedResult {
  cid: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileUploadedAcknowledgedResult {
  messageId: string;
  sender: string;
  messageTimestamp: number;
}

export interface CheckForKeysNeedingRegeneratingResult {
  sixId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationStartedResult {
  cid: string;
  sixId: string;
  totalBytes: number;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationProgressResult {
  cid: string;
  sixId: string;
  stage: 'fetching' | 'registering';
  bytesTransferred: number;
  totalBytes: number;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationFailedResult {
  cid: string;
  sixId: string;
  reason: string;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

export type MessageHandlerResult =
  | { type: 'file-ready-for-download'; data: FileUploadedResult }
  | { type: 'files-updated'; data: FilesUpdatedResult }
  | { type: 'folder-created'; data: FolderCreatedResult }
  | { type: 'folder-deleted'; data: FolderDeletedResult }
  | { type: 'file-deleted'; data: FileDeletedResult }
  | { type: 'six-created'; data: SixCreatedResult }
  | { type: 'six-updated'; data: SixUpdatedResult }
  | { type: 'six-deleted'; data: SixDeletedResult }
  | { type: 'file-cloned'; data: FileClonedResult }
  | {
      type: 'file-ready-for-download-acknowledged';
      data: FileUploadedAcknowledgedResult;
    }
  | {
      type: 'file-key-ready-for-download-acknowledged';
      data: FileUploadedAcknowledgedResult;
    }
  | {
      type: 'check-for-keys-needing-regenerating';
      data: CheckForKeysNeedingRegeneratingResult;
    }
  | { type: 'file-replication-started'; data: FileReplicationStartedResult }
  | { type: 'file-replication-progress'; data: FileReplicationProgressResult }
  | { type: 'file-replication-failed'; data: FileReplicationFailedResult };

export interface MessageStats {
  fromPeer: string;
  topic: string;
  timestamp: number;
  messageType: string;
  messageContent?: string;
}

export abstract class MessagingService {
  abstract setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void;
  abstract processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null>;
  abstract publishMessage(
    libp2pNode: LibP2pWithServices,
    topic: string,
    message: MessagePayload
  ): Promise<void>;
  abstract publishTopicList(
    libp2pNode: LibP2pWithServices,
    sixId: string,
    topics: string[]
  ): Promise<void>;
  abstract getSupportedTopics(): string[];
}
